import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import {
    IonPage,
    IonContent,
    IonCard,
    IonCardContent,
    IonLabel,
    IonItem,
    IonRadioGroup,
    IonRadio,
    IonButton,
    IonText,
    IonAlert,
    IonModal,
} from "@ionic/react";

// Question Interface
interface QuestionData {
    sentence: string;
    correctArticle: string;
    options: string[];
}


const FillBlanks: React.FC = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const modal = useRef<HTMLIonModalElement>(null);

    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    };

    const handleSubmit = (option: React.SetStateAction<string | null>) => {
        setSelectedOption(option);
        const currentQuestion = questions[currentIndex];
        if (option === currentQuestion.correctArticle) {
            // Automatically move to the next question
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    setShowImage(false);
                    setSelectedOption(null);
                }
                else {
                    setCurrentIndex(0);
                    fetchQuestions();
                }
            }, 5000); // Delay reduced to 3 seconds
        }
    };

    const fetchQuestions = async () => {
        try {
            let data: QuestionData[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'Articles.json');
                data = await response.json();
            } else {
                const module = await import('../../../../datas/Articles.json');
                data = module.default;
            }

            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    useEffect(() => {
        fetchQuestions();
        document.title = "Fill in the Blanks with A or An – LKG Grammar Worksheet | Mama’s Guidance"; // Set page title
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content='Enhance grammar skills in LKG kids with the Fill in the Blanks activity on Mama’s Guidance—focused on using articles (a, an, the) through simple and engaging sentence-based exercises.' />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart showCenter fillPreposition additionWord">
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Fill in the Blanks with A or An</h1>

                                <p>
                                    Let your little learner explore basic English grammar with this exciting <strong>Fill in the Blanks with A or An</strong> worksheet! Designed for LKG children, this activity helps them understand how to <strong>use the correct article</strong> before a word based on its starting sound.
                                </p>

                                <h2>🔤 What’s in the Worksheet?</h2>
                                <p>
                                    Each question presents a word or picture (e.g. <b><em>apple, ball, orange</em></b>), and the child must <strong>fill in the blank with the correct article – “a“ or “an“</strong>. The worksheet uses bright images and simple sentences to keep the activity fun and age-appropriate.
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Understand the basic use of articles: <strong>“a” and “an”</strong></li>
                                    <li>Differentiate between consonant and vowel sounds</li>
                                    <li>Improve sentence formation and grammar</li>
                                    <li>Enhance <strong>reading, writing, and thinking skills</strong></li>
                                </ul>

                                <h2>👩‍🏫 How to Use the Worksheet</h2>
                                <ul>
                                    <li>Say the word aloud with your child.</li>
                                    <li>Ask them to listen to the starting sound – is it a vowel or consonant?</li>
                                    <li>Help them choose the correct article (<strong>“a” for consonants, “an” for vowels</strong>).</li>
                                    <li>Let them write or circle the correct answer.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/english-language/one-and-many"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>One and Many</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/english-language/rhyming-words"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Rhyming Words</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/english-language/opposites"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Opposites</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why This Activity Is Helpful</h2>
                                <p>
                                    Understanding the correct use of <strong>“a“ and “an“</strong> builds confidence in sentence structure and grammar at an early age. With consistent practice, children start noticing patterns in sounds and word formation.
                                </p>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Fill in the Blanks with A or An</h1>

                            <p>
                                Let your little learner explore basic English grammar with this exciting <strong>Fill in the Blanks with A or An</strong> worksheet! Designed for LKG children, this activity helps them understand how to <strong>use the correct article</strong> before a word based on its starting sound.
                            </p>

                            <h2>🔤 What’s in the Worksheet?</h2>
                            <p>
                                Each question presents a word or picture (e.g. <em>apple, ball, orange</em>), and the child must <strong>fill in the blank with the correct article – “a“ or “an“</strong>. The worksheet uses bright images and simple sentences to keep the activity fun and age-appropriate.
                            </p>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Understand the basic use of articles: <strong>“a” and “an”</strong></li>
                                <li>Differentiate between consonant and vowel sounds</li>
                                <li>Improve sentence formation and grammar</li>
                                <li>Enhance <strong>reading, writing, and thinking skills</strong></li>
                            </ul>

                            <h2>👩‍🏫 How to Use the Worksheet</h2>
                            <ul>
                                <li>Say the word aloud with your child.</li>
                                <li>Ask them to listen to the starting sound – is it a vowel or consonant?</li>
                                <li>Help them choose the correct article (<strong>“a” for consonants, “an” for vowels</strong>).</li>
                                <li>Let them write or circle the correct answer.</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/lkg/english-language/one-and-many"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>One and Many</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/lkg/english-language/rhyming-words"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Rhyming Words</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/lkg/english-language/opposites"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Opposites</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🧠 Why This Activity Is Helpful</h2>
                            <p>
                                Understanding the correct use of <strong>“a“ and “an“</strong> builds confidence in sentence structure and grammar at an early age. With consistent practice, children start noticing patterns in sounds and word formation.
                            </p>
                        </article>
                        <>
                            {/* <h4>Fill in the blank with the correct article:</h4> */}
                            <p>
                                <strong>Sentence:</strong> &nbsp;&nbsp;<IonText>{questions[currentIndex]?.sentence}</IonText>
                            </p>

                            <IonRadioGroup
                                value={selectedOption}
                                onIonChange={(e) => handleSubmit(e.detail.value)}
                            >
                                {questions[currentIndex]?.options.map((option, index) => (
                                    <IonItem
                                        className={` ${selectedOption === option
                                            ? option === questions[currentIndex]?.correctArticle
                                                ? "correct"
                                                : "errorItems"
                                            : ""
                                            }`}
                                        key={index}>
                                        <IonLabel>{option}</IonLabel>
                                        <IonRadio slot="start" value={option} />
                                    </IonItem>
                                ))}
                            </IonRadioGroup>
                        </>

                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            style={{ height: "150px" }}
                            className="img-fluid"
                            src={process.env.REACT_APP_WELDONE_LINK}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default FillBlanks;
